import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, ListPlus } from 'lucide-react';
import { toast } from 'sonner';
import {
  useDailyWaitlist,
  useAddWaitlistEntry,
  useResolveWaitlistEntry,
  type WaitlistReason,
  type WaitlistResolution,
} from '@/hooks/useDailyWaitlist';

interface DailyWaitlistPanelProps {
  siteId: string;
}

const REASON_LABEL: Record<WaitlistReason, string> = {
  late_return: 'Late Return',
  returnee: 'Returnee',
  overage: 'Overage',
};

const RESOLUTION_LABEL: Record<WaitlistResolution, string> = {
  pending: 'Pending',
  rebedded: 'Rebedded',
  transferred: 'Transferred',
  left: 'Left',
};

export const DailyWaitlistPanel = ({ siteId }: DailyWaitlistPanelProps) => {
  const today = new Date().toISOString().slice(0, 10);
  const { data: entries = [] } = useDailyWaitlist(siteId, today);
  const addMut = useAddWaitlistEntry();
  const resolveMut = useResolveWaitlistEntry();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [reason, setReason] = useState<WaitlistReason>('late_return');
  const [notes, setNotes] = useState('');

  const submit = () => {
    if (!name.trim()) {
      toast.error('Enter the arrival name');
      return;
    }
    addMut.mutate(
      { site_id: siteId, arrival_name: name.trim(), reason, notes: notes.trim() || undefined },
      {
        onSuccess: () => {
          toast.success('Added to Daily Waitlist');
          setOpen(false);
          setName('');
          setReason('late_return');
          setNotes('');
        },
        onError: (e: any) => toast.error(e.message || 'Failed to add to waitlist'),
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ListPlus className="h-4 w-4" /> Daily Waitlist
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Late returns, returnees, and overages for today.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2">
                <Plus className="h-4 w-4" /> Add to Waitlist
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add to Daily Waitlist</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Arrival Name</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>Reason</Label>
                  <Select value={reason} onValueChange={(v) => setReason(v as WaitlistReason)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="late_return">Late Return</SelectItem>
                      <SelectItem value="returnee">Returnee</SelectItem>
                      <SelectItem value="overage">Overage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={addMut.isPending}>
                  {addMut.isPending ? 'Adding…' : 'Add'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground">
            No waitlist entries today.
          </div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-2 font-medium">Arrival</th>
                  <th className="text-left p-2 font-medium w-32">Time</th>
                  <th className="text-left p-2 font-medium w-32">Reason</th>
                  <th className="text-left p-2 font-medium w-40">Resolution</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-t">
                    <td className="p-2">
                      <div className="font-medium">{e.arrival_name}</div>
                      {e.notes && <div className="text-xs text-muted-foreground">{e.notes}</div>}
                    </td>
                    <td className="p-2 text-muted-foreground">
                      {new Date(e.arrival_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="p-2">
                      <Badge variant="outline">{REASON_LABEL[e.reason]}</Badge>
                    </td>
                    <td className="p-2">
                      <Select
                        value={e.resolution}
                        onValueChange={(v) =>
                          resolveMut.mutate(
                            { id: e.id, resolution: v as WaitlistResolution, siteId, notes: e.notes ?? undefined },
                            {
                              onSuccess: () => toast.success('Updated'),
                              onError: (err: any) => toast.error(err.message || 'Update failed'),
                            }
                          )
                        }
                      >
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="rebedded">Rebedded</SelectItem>
                          <SelectItem value="transferred">Transferred</SelectItem>
                          <SelectItem value="left">Left</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
